import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Loader, ChevronDown, ChevronUp } from "lucide-react";
import { FrameVec3EditorSection } from "./FrameVec3EditorSection";
import { calculateOpticalCenter } from "utils/math/calculateOpticalCenter";
import type { FrameStateEditorProps, HandleVec3Change } from "./types";

export const FrameStateEditor = ({
  frameName,
  frame,
  isUpdating,
  setEditedFrameFields,
  handleCancelEditing,
  handleUpdate,
  step = 0.01,
}: FrameStateEditorProps) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(true);

  const vec3EditSections = useMemo(
    () =>
      [
        { key: "position", title: t("doc_viewer.vec3_sections.translation") },
        { key: "rotation", title: t("doc_viewer.vec3_sections.rotation") },
      ] as const,
    [t]
  );

  const handleVec3Change: HandleVec3Change = useCallback(
    (vectorKey, vector) => {
      const translation = vectorKey === "position" ? vector : frame.position;
      const rotation = vectorKey === "rotation" ? vector : frame.rotation;

      const opticalCenter = calculateOpticalCenter({ translation, rotation });

      setEditedFrameFields({
        [vectorKey]: vector,
        processedPosition: opticalCenter,
      });
    },
    [frame.position, frame.rotation, setEditedFrameFields]
  );

  return (
    <div className="absolute top-4 left-4 w-72 bg-white p-4 rounded-lg shadow-lg border z-50">
      <div className="text-sm font-semibold mb-3 text-gray-700">
        {t("doc_viewer.edit_frame_state")}
      </div>
      <div className="flex items-center justify-between text-xs text-gray-500">
        <span>Frame: {frameName}</span>
        <button
          type="button"
          onClick={() => setIsOpen((v) => !v)}
          className="flex items-center"
        >
          {isOpen ? (
            <ChevronUp className="w-5 h-5" />
          ) : (
            <ChevronDown className="w-5 h-5" />
          )}
        </button>
      </div>
      {isOpen && (
        <div className="mt-2">
          {vec3EditSections.map(({ key, title }) => (
            <FrameVec3EditorSection
              key={key}
              title={title}
              fieldKey={key}
              vector={frame[key]}
              step={step}
              onChange={handleVec3Change}
            />
          ))}
          <div className="flex gap-2 pt-2">
            <button
              onClick={handleUpdate}
              disabled={isUpdating}
              className="flex-1 px-3 py-2 bg-green-500 text-white rounded hover:bg-green-600 disabled:opacity-50 text-sm"
            >
              {isUpdating ? (
                <div className="flex items-center justify-center">
                  <Loader className="w-3 h-3 animate-spin mr-1" />
                  {t("doc_viewer.updating")}
                </div>
              ) : (
                t("doc_viewer.update")
              )}
            </button>
            <button
              onClick={handleCancelEditing}
              disabled={isUpdating}
              className="flex-1 px-3 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 disabled:opacity-50 text-sm"
            >
              {t("doc_viewer.cancel")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
